import { Directive, ElementRef, EventEmitter, inject, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';

import { ScheduleNActionService } from '../../../+authenticated/+schedule/shared/schedule-actions/schedule-n-action.service';
import { DropZoneType } from '../../../enums';
import { DragDropConfig, getDropZoneParent, getDropZoneType } from '../config/drag-drop-config';
import { DragDropData } from '../models/drag-drop-data.model';
import { DragDropService } from '../service/drag-drop/drag-drop.service';
import { isPresent } from '../util/is-present';

@Directive({ standalone: false, selector: '[dnd-free-droppable]' })
export class FreeDroppableDirective implements OnInit, OnDestroy {
  private elementReference = inject(ElementRef);
  private dragDropService = inject(DragDropService);
  private scheduleActionService = inject(ScheduleNActionService);
  private config = inject(DragDropConfig);
  #zone = inject(NgZone);

  @Output()
  onDropSuccess: EventEmitter<DragDropData> = new EventEmitter<DragDropData>();
  @Output()
  onDragEnter: EventEmitter<DragDropData> = new EventEmitter<DragDropData>();
  @Output()
  onDragLeave: EventEmitter<DragDropData> = new EventEmitter<DragDropData>();

  private dataSubs = new Subscription();

  // The copy/move zone last handled within `lastEnterElement`. `dragover` keeps
  // firing while the pointer rests on one zone, and every event would otherwise
  // strip and re-add the same classes, invalidating style each time.
  #lastDropTarget: HTMLElement | null = null;

  ngOnInit() {
    this.dataSubs.add(
      this.dragDropService.isDragging.subscribe(() => {
        this.#lastDropTarget = null;
      }),
    );

    // `dragover` fires continuously during a drag. It only touches DOM classes and
    // `isCopyingSubject`, whose consumer runs change detection itself, so it never
    // needs an application tick.
    this.#zone.runOutsideAngular(() => {
      this.dataSubs.add(
        fromEvent(this.elementReference.nativeElement, 'dragover').subscribe((event: DragEvent) => {
          event.preventDefault();
          this.dragOverCallback(event);

          if (isPresent(event.dataTransfer)) {
            event.dataTransfer.dropEffect = this.config.dropEffect.name;
          }

          return false;
        }),
      );
    });

    this.dataSubs.add(
      fromEvent(this.elementReference.nativeElement, 'drop').subscribe((event: DragEvent) => {
        if (this.dragDropService.isDropAllowed()) {
          event.preventDefault();
          event.stopPropagation();

          this.dropCallback(event);
        }
        // Reset drag state from the destination so dropzone classes (e.g. dnd-drop-entered)
        // are cleared even when the source element is destroyed by an optimistic store update
        // before the browser fires `dragend` on it.
        this.dragDropService.isDragged = false;
      }),
    );
  }

  ngOnDestroy() {
    this.dataSubs.unsubscribe();
  }

  dragOverCallback(event: DragEvent): void {
    if (!this.dragDropService.isDragged) {
      return;
    }

    let target: any = event.target;
    target = target.nodeName !== 'DIV' ? target.closest('div') : target;
    const dropZoneType = getDropZoneType(target);
    const parent = getDropZoneParent(event);

    if (dropZoneType === DropZoneType.NONE) {
      this.#lastDropTarget = null;
      this.scheduleActionService.isCopyingSubject.next(false);
      return;
    }

    if (this.dragDropService.lastEnterElement === parent && this.#lastDropTarget === target) {
      return;
    }

    if (this.dragDropService.lastEnterElement !== parent) {
      this.dragDropService.resetLastElement();
    }

    this.dragDropService.resetLastElement(false);
    this.dragDropService.lastEnterElement = parent;
    this.#lastDropTarget = target;

    if (!this.dragDropService.isDropAllowed()) {
      parent.classList.add(this.config.onDragDeniedClass);
      this.scheduleActionService.isCopyingSubject.next(false);
      return;
    }

    if (this.dragDropService.hasSkillsWarning()) {
      parent.classList.add(this.config.onDragWarningClass);
    } else {
      parent.classList.remove(this.config.onDragWarningClass);
    }

    parent.classList.add(this.config.onDropEntered);
    target.classList.add(this.config.onDropEntered);
    this.scheduleActionService.isCopyingSubject.next(dropZoneType === DropZoneType.COPY);
  }

  dropCallback(event: MouseEvent): void {
    const dataTransfer = (event as any).dataTransfer;
    let target: any = event.target;

    target = target.nodeName !== 'DIV' ? target.closest('div') : target;
    const dropZoneType = getDropZoneType(target);
    const parent = getDropZoneParent(event);

    const isCopying = dropZoneType === DropZoneType.COPY;
    if (dropZoneType === DropZoneType.NONE) {
      return;
    }

    if (!this.dragDropService.isDropAllowed()) {
      return;
    }

    this.dragDropService.lastEnterElement = parent;

    if (this.dragDropService.isDragged || (isPresent(dataTransfer) && isPresent(dataTransfer.files))) {
      this.onDropSuccess.emit({
        dragData: { ...this.dragDropService.dragData, isCopying },
        mouseEvent: event,
        dropZoneData: this.dragDropService.getDataSet() ?? undefined,
      });

      if (isPresent(this.dragDropService.onDragSuccessCallback)) {
        this.dragDropService.onDragSuccessCallback.emit({ dragData: this.dragDropService.dragData, mouseEvent: event });
      }
    }
  }
}
